/**
 * Enstrüman açıklaması + SSS içeriğinin YEREL üretimi.
 *
 * Ağustos 2026 denetimi: bu içerik daha önce `/api/instruments/{symbol}/content`
 * ucundan çekiliyordu ama o uç backend'de hiç yok (InstrumentContent entity'si
 * 6f5a89e ile geri alınmıştı). Sonuç: her enstrüman sayfası açılışında boşuna bir
 * 404 isteği atılıyor, InstrumentAbout hiçbir zaman render edilmiyor ve MEMORY'de
 * "tamamlanmış" görünen FAQPage JSON-LD üretimde hiç çalışmıyordu.
 *
 * İçerik artık istemciye ağ isteği olmadan, ilk render'da hazır geliyor — bu sayede
 * hem metin hem FAQ şeması SSR HTML'inde yer alır ve indekslenebilir.
 *
 * Metinler bilinçli olarak TANIMLAYICIDIR: fiyat tahmini, getiri vaadi veya yatırım
 * tavsiyesi içermez (YMYL).
 */

export interface FaqItem {
  q: string
  a: string
}

export interface InstrumentContent {
  description: string
  faq: FaqItem[]
}

export type InstrumentKind = 'stock' | 'crypto' | 'forex' | 'index'

const TR: Record<InstrumentKind, (s: string) => InstrumentContent> = {
  stock: s => ({
    description: `${s}, Borsaya.com üzerinde canlı fiyat, günlük değişim, işlem hacmi ve tarihsel grafik verileriyle takip edilebilen bir hisse senedidir. Bu sayfadaki veriler ilgili borsanın seans saatlerine göre güncellenir.`,
    faq: [
      { q: `${s} fiyatı ne sıklıkla güncelleniyor?`, a: 'Fiyatlar borsa seansı boyunca düzenli olarak güncellenir. Seans dışında son kapanış değeri gösterilir. Veriler bilgilendirme amaçlıdır ve gecikmeli olabilir.' },
      { q: `${s} grafiğinde hangi dönemleri görebilirim?`, a: 'Grafik üzerindeki dönem düğmeleriyle 1 gün, 1 hafta, 1 ay, 1 yıl ve 5 yıllık geçmişi inceleyebilirsiniz.' },
      { q: 'Bu sayfadaki veriler yatırım tavsiyesi mi?', a: 'Hayır. Borsaya.com yalnızca piyasa verisi sunar; hiçbir içerik alım, satım veya elde tutma tavsiyesi değildir.' },
    ],
  }),
  crypto: s => ({
    description: `${s}, Borsaya.com üzerinde canlı fiyat, 24 saatlik değişim ve tarihsel grafik verileriyle takip edilebilen bir kripto varlıktır. Kripto piyasaları haftanın yedi günü kesintisiz işlem görür.`,
    faq: [
      { q: `${s} fiyatı hafta sonu da değişir mi?`, a: 'Evet. Kripto piyasaları 7/24 açıktır; fiyat hafta sonu ve resmî tatillerde de değişmeye devam eder.' },
      { q: `${s} fiyatı hangi para biriminde gösteriliyor?`, a: 'Fiyatlar varsayılan olarak ABD doları (USD) cinsinden gösterilir.' },
      { q: 'Bu sayfadaki veriler yatırım tavsiyesi mi?', a: 'Hayır. Kripto varlıklar yüksek oynaklık taşır. Borsaya.com yalnızca piyasa verisi sunar, yatırım tavsiyesi vermez.' },
    ],
  }),
  forex: s => ({
    description: `${s} paritesi, Borsaya.com üzerinde canlı kur, günlük değişim ve tarihsel grafik verileriyle takip edilebilir. Döviz piyasası hafta içi kesintisiz işlem görür.`,
    faq: [
      { q: `${s} kuru ne zaman güncellenir?`, a: 'Döviz piyasası pazartesi sabahından cuma akşamına kadar kesintisiz açıktır; kur bu süre boyunca güncellenir.' },
      { q: `${s} paritesi neyi ifade eder?`, a: 'Parite, ilk para biriminin bir biriminin ikinci para birimi cinsinden değerini gösterir.' },
      { q: 'Bu sayfadaki veriler yatırım tavsiyesi mi?', a: 'Hayır. Gösterilen kurlar bilgilendirme amaçlıdır ve alım satım işlemlerinde uygulanan kurlardan farklı olabilir.' },
    ],
  }),
  index: s => ({
    description: `${s} endeksi, kapsadığı şirketlerin toplu performansını ölçen bir gösterge endeksidir. Borsaya.com üzerinde canlı değer, günlük değişim ve tarihsel grafik verileriyle takip edilebilir.`,
    faq: [
      { q: `${s} endeksi neyi ölçer?`, a: 'Endeks, kapsamındaki şirketlerin hisse fiyatlarındaki değişimi tek bir sayıya indirger ve o piyasanın genel yönü hakkında fikir verir.' },
      { q: `${s} verileri ne sıklıkla güncelleniyor?`, a: 'İlgili borsanın seans saatleri boyunca düzenli olarak güncellenir; seans dışında son kapanış değeri gösterilir.' },
      { q: 'Bu sayfadaki veriler yatırım tavsiyesi mi?', a: 'Hayır. Borsaya.com yalnızca piyasa verisi sunar; hiçbir içerik yatırım tavsiyesi niteliği taşımaz.' },
    ],
  }),
}

const EN: Record<InstrumentKind, (s: string) => InstrumentContent> = {
  stock: s => ({
    description: `${s} is a stock you can follow on Borsaya.com with live prices, daily change, trading volume and historical charts. Data on this page updates during the relevant exchange's trading hours.`,
    faq: [
      { q: `How often is the ${s} price updated?`, a: 'Prices update regularly throughout the trading session. Outside session hours the last closing value is shown. Data is for information only and may be delayed.' },
      { q: `Which time ranges are available on the ${s} chart?`, a: 'Use the period buttons on the chart to view 1 day, 1 week, 1 month, 1 year and 5 year history.' },
      { q: 'Is the data on this page investment advice?', a: 'No. Borsaya.com provides market data only; nothing here is a recommendation to buy, sell or hold.' },
    ],
  }),
  crypto: s => ({
    description: `${s} is a crypto asset you can follow on Borsaya.com with live prices, 24-hour change and historical charts. Crypto markets trade continuously, seven days a week.`,
    faq: [
      { q: `Does the ${s} price change on weekends?`, a: 'Yes. Crypto markets are open 24/7, so the price keeps moving on weekends and public holidays.' },
      { q: `Which currency is the ${s} price shown in?`, a: 'Prices are shown in US dollars (USD) by default.' },
      { q: 'Is the data on this page investment advice?', a: 'No. Crypto assets are highly volatile. Borsaya.com provides market data only and does not give investment advice.' },
    ],
  }),
  forex: s => ({
    description: `The ${s} pair can be followed on Borsaya.com with live rates, daily change and historical charts. The foreign exchange market trades continuously on weekdays.`,
    faq: [
      { q: `When is the ${s} rate updated?`, a: 'The FX market is open continuously from Monday morning to Friday evening; the rate updates throughout that window.' },
      { q: `What does the ${s} pair represent?`, a: 'A pair shows the value of one unit of the first currency expressed in the second currency.' },
      { q: 'Is the data on this page investment advice?', a: 'No. Rates shown are for information only and may differ from the rates applied in actual transactions.' },
    ],
  }),
  index: s => ({
    description: `The ${s} index is a benchmark that measures the combined performance of its constituent companies. It can be followed on Borsaya.com with live values, daily change and historical charts.`,
    faq: [
      { q: `What does the ${s} index measure?`, a: 'The index condenses price changes across its constituent companies into a single number, giving a sense of the overall market direction.' },
      { q: `How often is ${s} data updated?`, a: "It updates regularly during the relevant exchange's trading hours; outside those hours the last closing value is shown." },
      { q: 'Is the data on this page investment advice?', a: 'No. Borsaya.com provides market data only; no content constitutes investment advice.' },
    ],
  }),
}

export function getInstrumentContent(
  symbol: string,
  kind: InstrumentKind,
  locale: string,
): InstrumentContent | null {
  const clean = (symbol || '').trim().toUpperCase()
  if (!clean) return null
  return (locale === 'en' ? EN : TR)[kind](clean)
}
